/*
 * Figma `108:2` — SETUP-01 · Tickets · Registry + Edit Drawer.
 *
 * `TableTemplate`: the totals, the status chips, the registry as a table, and the type you opened
 * in a drawer beside it. The shipped panel edits every type inline in one long column, which is
 * where the frame's change comes from — at eight ticket types the prices, quotas, custom fields and
 * wristband bindings of all eight are on screen at once and none of them is legible.
 *
 * What is *not* changed is the save model. Edits still accumulate as a draft per type and one bar
 * commits them all, keeping the drafts of whichever rows the server rejected. That behaviour is
 * careful and the frame does not ask for it to change — only for the editing to happen somewhere
 * a person can read it. So the drawer edits the same draft the old rows edited, and the same button
 * sends the same requests.
 *
 * `sold` is the number that makes this screen consequential: capacity below what is already sold is
 * refused by the server, and the drawer says so next to the field rather than letting the save
 * come back rejected.
 */
import { useState } from 'react';
import type { ReactNode } from 'react';
import {
  Alert, Button, Card, Drawer, FilterChips, FormDialog, KpiCard, SaveBar, SearchField, Select,
  StatusBadge, TableTemplate, TextField, Toggle,
} from '..';
import type { Column, SaveState } from '..';
import type { Destination } from '../destinations';
import type { AdminTicketDto, TicketInput, WristbandBinding } from '../../api';
import type { TicketsState } from './useTickets';
import { useTickets } from './useTickets';
import { FREE_TICKET_NOTICE, isFreeOnlineTicket } from '../../lib/freeTicket';

type Tab = 'all' | 'onsale' | 'offline';

const baht = (n: number) => `฿${n.toLocaleString('th-TH')}`;

/*
 * What a new type needs before it can exist, and nothing more. Everything else — wristband
 * bindings, custom fields, whether it is sold at a counter — is edited in the drawer, which is
 * where this screen already does its editing. A create form that reproduced the whole drawer
 * would be a second place for the same decisions to drift apart.
 */
const BLANK_TICKET: TicketInput = {
  nameEn: '', nameTh: '', priceRegular: 0, priceEarly: 0, capacity: 0, seats: 1, sort: 0,
  visible: true, promotion: false, offline: false, serialPrefix: null, customFields: [],
};

export function TicketsScreen({ destination, state, permitted, role, extra }: {
  destination: Destination;
  state: TicketsState;
  permitted: boolean;
  role?: string | null;
  /** Coupons and printed batches, which are their own panels in this codebase. */
  extra?: ReactNode;
}) {
  const {
    tickets, wristbands, drafts, bandDrafts, loading, loadError,
    saving, justSaved, saveError, anyDirty, isDirty, setDraft, setBandDraft, discard, saveAll,
    creating, createError, create,
  } = state;
  const [tab, setTab] = useState<Tab>('all');
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draftNew, setDraftNew] = useState(BLANK_TICKET);

  const open = openId ? tickets.find((t) => t.id === openId) ?? null : null;
  const draft = open ? drafts[open.id] : undefined;

  const visible = tickets.filter((t) => {
    if (tab === 'onsale' && t.offline) return false;
    if (tab === 'offline' && !t.offline) return false;
    if (!query.trim()) return true;
    const needle = query.trim().toLowerCase();
    return `${t.nameTh} ${t.nameEn}`.toLowerCase().includes(needle);
  });

  const totalCapacity = tickets.reduce((sum, t) => sum + t.capacity, 0);
  const totalSold = tickets.reduce((sum, t) => sum + t.sold, 0);
  const revenue = tickets.reduce((sum, t) => sum + t.sold * t.priceRegular, 0);

  const saveState: SaveState =
    saveError ? 'error' : saving ? 'saving' : anyDirty ? 'dirty' : justSaved ? 'saved' : 'clean';

  const columns: Column<AdminTicketDto>[] = [
    {
      key: 'name',
      header: 'ประเภทบัตร',
      cell: (t) => (
        <span className="jt-cell-stack">
          <span>{t.nameTh || t.nameEn}</span>
          <span className="jt-cell-sub">{t.nameEn}</span>
        </span>
      ),
    },
    {
      key: 'price',
      header: 'ราคา',
      align: 'end',
      width: '150px',
      cell: (t) => (
        <span className="jt-cell-stack">
          <span className="jt-mono">{baht(t.priceRegular)}</span>
          {t.priceEarly > 0 && t.priceEarly !== t.priceRegular && (
            <span className="jt-cell-sub jt-mono">Early {baht(t.priceEarly)}</span>
          )}
        </span>
      ),
    },
    {
      key: 'sold',
      header: 'ขายแล้ว / โควตา',
      align: 'end',
      width: '160px',
      cell: (t) => (
        <span className="jt-cell-stack">
          <span className="jt-mono">{t.sold.toLocaleString('th-TH')} ขายแล้ว / {t.capacity.toLocaleString('th-TH')}</span>
          {((t.reservedOnline ?? 0) > 0 || (t.reservedPrinted ?? 0) > 0) && <span className="jt-cell-sub">รอชำระ {t.reservedOnline ?? 0} · พิมพ์รอขาย {t.reservedPrinted ?? 0} · ใช้โควตา {t.committed ?? t.sold}</span>}
        </span>
      ),
    },
    {
      key: 'state',
      header: 'สถานะ',
      width: '210px',
      cell: (t) => (
        <span className="jt-inline-actions">
          {t.offline
            ? <StatusBadge tone="neutral">ขายหน้างาน</StatusBadge>
            : t.soldOut
              ? <StatusBadge tone="danger">ขายหมด</StatusBadge>
              : <StatusBadge tone="success">เปิดขาย</StatusBadge>}
          {isDirty(t.id) && <StatusBadge tone="warning">ยังไม่บันทึก</StatusBadge>}
        </span>
      ),
    },
  ];

  const bandRows: WristbandBinding[] = open ? bandDrafts[open.id] ?? [] : [];

  return (
    <TableTemplate
      gate={{ destination, loading, error: loadError || null, permitted, role }}
      kpis={[
        <KpiCard key="types" label="ประเภทบัตร" value={String(tickets.length)} />,
        <KpiCard key="cap" label="โควตารวม" value={totalCapacity.toLocaleString('th-TH')} />,
        <KpiCard
          key="sold"
          label="ขายแล้ว"
          value={totalSold.toLocaleString('th-TH')}
          meta={totalCapacity > 0 ? `${Math.round((totalSold / totalCapacity) * 100)}% ของโควตา` : undefined}
        />,
        <KpiCard key="rev" label="รายได้ตามราคาปกติ" value={baht(revenue)} />,
      ]}
      toolbar={
        <>
          <FilterChips
            chips={[
              { key: 'all', label: 'ทั้งหมด', count: tickets.length },
              { key: 'onsale', label: 'ขายออนไลน์', count: tickets.filter((t) => !t.offline).length },
              { key: 'offline', label: 'ขายหน้างาน', count: tickets.filter((t) => t.offline).length },
            ]}
            active={tab}
            onSelect={(key) => setTab(key as Tab)}
            label="ชนิดการขาย"
          />
          <SearchField value={query} onChange={setQuery} label="ค้นหาประเภทบัตร" />
          {permitted && (
            <Button size="sm" variant="primary" onClick={() => { setDraftNew(BLANK_TICKET); setAdding(true); }}>
              ＋ สร้างประเภทตั๋ว
            </Button>
          )}
        </>
      }
      table={{
        label: 'ประเภทบัตร',
        columns,
        rows: visible,
        rowKey: (t) => t.id,
        filtered: tab !== 'all' || query.trim().length > 0,
        onClearFilters: () => { setTab('all'); setQuery(''); },
        emptyTitle: 'ยังไม่มีประเภทบัตร',
        emptyText: 'เพิ่มประเภทบัตรอย่างน้อยหนึ่งแบบ ผู้ซื้อจึงจะซื้อได้',
        onRowClick: (t) => setOpenId(t.id),
        activeRowKey: openId,
        card: (t) => (
          <span className="jt-cell-stack">
            <span>{t.nameTh || t.nameEn}</span>
            <span className="jt-cell-sub jt-mono">{baht(t.priceRegular)} · {t.sold}/{t.capacity}</span>
          </span>
        ),
      }}
      drawer={
        <>
          {/*
            * The registry had no way to add a type at all, which on a freshly provisioned event
            * meant the console could not be used to sell anything: `useTickets` has carried
            * `create` since the rebuild and nothing ever called it.
            */}
          <FormDialog
            open={adding}
            onClose={() => setAdding(false)}
            onSubmit={() => {
              void create(draftNew).then((ok) => { if (ok) setAdding(false); });
            }}
            title="สร้างประเภทตั๋ว"
            description="ตั้งชื่อ ราคา และโควตาให้พอขายได้ก่อน — ที่เหลือแก้ต่อในรายละเอียดของประเภทนั้น"
            submitLabel="สร้างประเภทตั๋ว"
            busy={creating}
            error={createError || null}
          >
            <TextField
              label="ชื่อที่ผู้ซื้อเห็น"
              value={draftNew.nameTh}
              onChange={(e) => setDraftNew({ ...draftNew, nameTh: e.target.value })}
              hint="เช่น บัตรยืน โซน A"
            />
            <TextField
              label="ชื่อภาษาอังกฤษ"
              value={draftNew.nameEn}
              onChange={(e) => setDraftNew({ ...draftNew, nameEn: e.target.value })}
            />
            <TextField
              label="ราคาปกติ (บาท)"
              type="number"
              value={String(draftNew.priceRegular)}
              onChange={(e) => setDraftNew({ ...draftNew, priceRegular: Number(e.target.value) || 0 })}
              hint={isFreeOnlineTicket(draftNew) ? FREE_TICKET_NOTICE : undefined}
            />
            <TextField
              label="ราคา Early Bird (บาท)"
              type="number"
              value={String(draftNew.priceEarly)}
              onChange={(e) => setDraftNew({ ...draftNew, priceEarly: Number(e.target.value) || 0 })}
              hint="ใส่ 0 ถ้าไม่มีราคา Early Bird"
            />
            <TextField
              label="โควตา"
              type="number"
              value={String(draftNew.capacity)}
              onChange={(e) => setDraftNew({ ...draftNew, capacity: Number(e.target.value) || 0 })}
              hint="จำนวนใบที่ขายได้ทั้งหมดของประเภทนี้"
            />
            <Toggle
              label="แสดงบนหน้าขาย"
              checked={draftNew.visible}
              onChange={(e) => setDraftNew({ ...draftNew, visible: e.target.checked })}
              consequence="ปิดไว้ได้ถ้ายังไม่อยากให้ผู้ซื้อเห็นระหว่างตั้งค่า"
            />
          </FormDialog>

          {extra}

          {anyDirty || saveError || justSaved ? (
            <SaveBar
              state={saveState}
              error={saveError || null}
              onSave={() => void saveAll()}
              saveLabel="บันทึกทั้งหมด"
              consequence="บันทึกพร้อมกันทุกประเภทที่แก้ไว้ — ประเภทที่บันทึกไม่สำเร็จจะยังเก็บสิ่งที่พิมพ์ไว้"
            />
          ) : null}

          <Drawer
            open={!!open && !!draft}
            onClose={() => setOpenId(null)}
            title={open ? `แก้ไข ${open.nameTh || open.nameEn}` : ''}
            /* The guard is the drawer's, but the save is still the bar's: closing keeps the draft,
             * it does not commit it. So "unsaved" here means "will be sent by the bar", and the
             * dirty marker on the row says the same thing. */
            dirty={false}
            footer={open && (
              <>
                <Button variant="ghost" disabled={!isDirty(open.id)} onClick={() => discard(open.id)}>
                  ย้อนกลับค่าเดิม
                </Button>
                <Button variant="primary" disabled={!anyDirty || saving} onClick={() => void saveAll()}>
                  {saving ? 'กำลังบันทึก…' : 'บันทึกทั้งหมด'}
                </Button>
              </>
            )}
          >
            {open && draft && (
              <>
                {isDirty(open.id) && (
                  <Alert tone="warning" title="ยังไม่ได้บันทึก">
                    การแก้ไขจะถูกส่งเมื่อกด “บันทึกทั้งหมด” — ปิดแผงนี้ไม่ทำให้หาย
                  </Alert>
                )}

                <Card title="ชื่อและราคา">
                  <div className="jt-field-grid">
                    <TextField
                      label="ชื่อ (ไทย)"
                      value={draft.nameTh}
                      onChange={(e) => setDraft(open.id, { nameTh: e.target.value })}
                    />
                    <TextField
                      label="ชื่อ (อังกฤษ)"
                      value={draft.nameEn}
                      onChange={(e) => setDraft(open.id, { nameEn: e.target.value })}
                    />
                  </div>
                  <div className="jt-field-grid">
                    <TextField
                      label="ราคาปกติ"
                      type="number"
                      min={0}
                      value={draft.priceRegular}
                      onChange={(e) => setDraft(open.id, { priceRegular: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                      hint={isFreeOnlineTicket(draft) ? FREE_TICKET_NOTICE : undefined}
                    />
                    <TextField
                      label="ราคา Early Bird"
                      type="number"
                      min={0}
                      value={draft.priceEarly}
                      onChange={(e) => setDraft(open.id, { priceEarly: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                      hint="ใส่ 0 ถ้าไม่มีราคาช่วงแรก"
                    />
                  </div>
                </Card>

                <Card title="โควตาและที่นั่ง">
                  <TextField
                    label="โควตา"
                    type="number"
                    min={open.committed ?? open.sold}
                    value={draft.capacity}
                    onChange={(e) => setDraft(open.id, { capacity: Math.max(0, Math.round(Number(e.target.value) || 0)) })}
                    error={draft.capacity < (open.committed ?? open.sold)
                      ? `ลดโควตาต่ำกว่า ${(open.committed ?? open.sold).toLocaleString('th-TH')} ใบที่ใช้โควตาอยู่ไม่ได้`
                      : null}
                    hint={`ขายแล้ว ${open.sold.toLocaleString('th-TH')} ใบ · ใช้โควตา ${(open.committed ?? open.sold).toLocaleString('th-TH')} ใบ`}
                  />
                  <TextField
                    label="จำนวนคนต่อบัตร"
                    type="number"
                    min={1}
                    value={draft.seats}
                    onChange={(e) => setDraft(open.id, { seats: Math.max(1, Math.round(Number(e.target.value) || 1)) })}
                    hint="บัตรคู่หรือบัตรกลุ่มใส่มากกว่า 1 — มีผลกับจำนวนคนที่เข้างานได้"
                  />
                  <Select
                    label="รูปแบบที่นั่ง"
                    value={draft.seatingMode ?? 'none'}
                    options={[
                      { value: 'none', label: 'ยืน / ไม่ระบุที่นั่ง' },
                      { value: 'zone_seated', label: 'ระบุที่นั่งรายตัวผ่านโซน' },
                    ]}
                    onChange={(e) => setDraft(open.id, { seatingMode: e.target.value as 'none' | 'zone_seated' })}
                  />
                </Card>

                <Card title="ช่องทางการขาย">
                  <Toggle
                    label="ขายหน้างานเท่านั้น"
                    checked={!!draft.offline}
                    onChange={(e) => setDraft(open.id, { offline: e.target.checked })}
                    consequence="ซ่อนจากหน้าขายบัตร และพิมพ์เป็นบัตรแข็งขายที่หน้างานแทน"
                  />
                  {draft.offline && (
                    <TextField
                      label="Serial prefix"
                      value={draft.serialPrefix ?? ''}
                      onChange={(e) => setDraft(open.id, { serialPrefix: e.target.value.toUpperCase() || null })}
                      hint="4 ตัวอักษร A–Z หรือ 0–9 ไม่ซ้ำกับประเภทอื่น — ต้องมีก่อนจึงพิมพ์บัตรได้"
                    />
                  )}
                  <Toggle
                    label="ติดป้ายโปรโมชัน"
                    checked={!!draft.promotion}
                    onChange={(e) => setDraft(open.id, { promotion: e.target.checked })}
                    consequence="แสดงป้ายเน้นบัตรประเภทนี้บนหน้าขาย"
                  />
                </Card>

                <Card
                  title="Wristband ที่แถมกับบัตรนี้"
                  sub={wristbands.length === 0 ? 'ยังไม่มี Wristband ให้ผูก — สร้างที่หน้า Wristband ก่อน' : undefined}
                >
                  {bandRows.map((row, i) => (
                    <div key={`${row.wristbandId}-${i}`} className="jt-field-grid">
                      <Select
                        label="Wristband"
                        value={row.wristbandId}
                        options={wristbands.map((w) => ({ value: w.id, label: w.name }))}
                        onChange={(e) => setBandDraft(open.id, bandRows.map((r, j) => (j === i ? { ...r, wristbandId: e.target.value } : r)))}
                      />
                      <TextField
                        label="จำนวนต่อบัตร"
                        type="number"
                        min={0}
                        value={row.qty}
                        onChange={(e) => setBandDraft(open.id, bandRows.map((r, j) => (j === i ? { ...r, qty: Math.max(0, Math.round(Number(e.target.value) || 0)) } : r)))}
                        trailing={
                          <Button size="sm" variant="ghost" aria-label={`ลบ Wristband แถวที่ ${i + 1}`}
                            onClick={() => setBandDraft(open.id, bandRows.filter((_, j) => j !== i))}>
                            ลบ
                          </Button>
                        }
                      />
                    </div>
                  ))}
                  {wristbands.length > 0 && (
                    <div className="jt-inline-actions">
                      <Button
                        size="sm"
                        onClick={() => setBandDraft(open.id, [...bandRows, { wristbandId: wristbands[0].id, qty: 1 }])}
                      >
                        ＋ เพิ่ม Wristband
                      </Button>
                    </div>
                  )}
                </Card>
              </>
            )}
          </Drawer>
        </>
      }
    />
  );
}

export function TicketsDestination({ token, eventSlug, destination, permitted, role, extra }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
  extra?: ReactNode;
}) {
  const state = useTickets(token, eventSlug);
  return (
    <TicketsScreen
      destination={destination} state={state} permitted={permitted} role={role} extra={extra}
    />
  );
}
